import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Cell, Pie, PieChart } from 'recharts';
import { Card, CardContent } from '@/components/ui/card';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '@/components/ui/chart';
import { FACULTY_COLORS, DEFAULT_NODE_COLOR } from '../graph/graphConfig';
import type { CtuCompany } from '../../types/api';

interface CTUCompaniesByFacultyChartProps {
  companies: CtuCompany[];
}

interface FacultyBucket {
  key: string;
  abbr: string;
  count: number;
  fill: string;
}

const OTHER_KEY = '__other__';

const RAD = Math.PI / 180;

function renderSliceLabel(props: any) {
  const { cx, cy, midAngle, outerRadius, payload } = props;
  const lineEnd = outerRadius + 12;
  const labelRadius = outerRadius + 18;
  const cos = Math.cos(-midAngle * RAD);
  const sin = Math.sin(-midAngle * RAD);
  const sx = cx + outerRadius * cos;
  const sy = cy + outerRadius * sin;
  const ex = cx + lineEnd * cos;
  const ey = cy + lineEnd * sin;
  const tx = cx + labelRadius * cos;
  const ty = cy + labelRadius * sin;
  const anchor = cos >= 0 ? 'start' : 'end';
  return (
    <g>
      <path d={`M${sx},${sy}L${ex},${ey}`} stroke="hsl(var(--border))" fill="none" />
      <text
        x={tx}
        y={ty}
        textAnchor={anchor}
        dominantBaseline="central"
        className="fill-foreground text-[11px] font-medium"
      >
        {payload.abbr} ({payload.count})
      </text>
    </g>
  );
}

export default function CTUCompaniesByFacultyChart({
  companies,
}: CTUCompaniesByFacultyChartProps) {
  const { t } = useTranslation('ctuCompanies');
  const otherLabel = t('pie.other');

  const buckets = useMemo<FacultyBucket[]>(() => {
    const counts = new Map<string, number>();
    for (const abbr of Object.keys(FACULTY_COLORS)) counts.set(abbr, 0);

    for (const c of companies) {
      const abbr = c.department?.faculty_abbr?.trim() || OTHER_KEY;
      counts.set(abbr, (counts.get(abbr) ?? 0) + 1);
    }

    return Array.from(counts.entries())
      .map(([key, count]) => ({
        key,
        abbr: key === OTHER_KEY ? otherLabel : key,
        count,
        fill: FACULTY_COLORS[key] || DEFAULT_NODE_COLOR,
      }))
      .sort((a, b) => {
        if (b.count !== a.count) return b.count - a.count;
        return a.abbr.localeCompare(b.abbr);
      });
  }, [companies, otherLabel]);

  const total = useMemo(() => buckets.reduce((s, d) => s + d.count, 0), [buckets]);
  const pieData = useMemo(() => buckets.filter((d) => d.count > 0), [buckets]);
  const listData = useMemo(() => buckets.filter((d) => d.count === 0), [buckets]);

  const chartConfig = useMemo<ChartConfig>(() => {
    const cfg: ChartConfig = { count: { label: t('pie.legend') } };
    for (const d of buckets) cfg[d.key] = { label: d.abbr, color: d.fill };
    return cfg;
  }, [buckets, t]);

  return (
    <Card className="border-ctu-border shadow-none mb-6">
      <CardContent className="flex flex-col gap-4 px-6 pt-5 pb-5">
        <div className="flex items-baseline gap-3 flex-wrap">
          <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ctu-text-muted">
            {t('pie.title')}
          </span>
          <span className="text-xs text-ctu-text-muted">{t('pie.subtitle')}</span>
        </div>

        {total === 0 ? (
          <div className="flex items-center justify-center py-10 text-sm text-ctu-text-muted">
            {t('table.empty')}
          </div>
        ) : (
          <div className="flex w-full flex-col items-center gap-6 sm:flex-row sm:items-center sm:justify-center">
            <ChartContainer
              config={chartConfig}
              className="aspect-square w-full max-w-[340px] shrink-0"
            >
              <PieChart>
                <ChartTooltip
                  cursor={false}
                  content={
                    <ChartTooltipContent
                      nameKey="abbr"
                      hideLabel
                      formatter={(value: any, _name: any, item: any) => {
                        const pct = total ? Math.round((Number(value) / total) * 100) : 0;
                        return (
                          <div className="flex w-full items-center justify-between gap-3">
                            <span className="flex items-center gap-2">
                              <span
                                className="size-2 shrink-0 rounded-[2px]"
                                style={{ backgroundColor: item?.payload?.fill }}
                              />
                              <span>{item?.payload?.abbr}</span>
                            </span>
                            <span className="font-mono tabular-nums text-foreground">
                              {value} <span className="text-ctu-text-muted">({pct}%)</span>
                            </span>
                          </div>
                        );
                      }}
                    />
                  }
                />
                <Pie
                  data={pieData}
                  dataKey="count"
                  nameKey="abbr"
                  innerRadius="38%"
                  outerRadius="62%"
                  paddingAngle={1.5}
                  strokeWidth={2}
                  stroke="hsl(var(--card))"
                  isAnimationActive={false}
                  label={renderSliceLabel}
                  labelLine={false}
                >
                  {pieData.map((d) => (
                    <Cell key={d.key} fill={d.fill} />
                  ))}
                </Pie>
              </PieChart>
            </ChartContainer>

            {listData.length > 0 && (
              <ul className="m-0 flex w-full flex-col divide-y divide-border list-none p-0 sm:max-w-[260px] opacity-50">
                {listData.map((d) => (
                  <li key={d.key} className="flex items-center gap-3 py-2 text-sm">
                    <span
                      className="size-2.5 shrink-0 rounded-[3px]"
                      style={{ backgroundColor: d.fill }}
                      aria-hidden
                    />
                    <span className="flex-1 min-w-0 truncate text-ctu-text">{d.abbr}</span>
                    <span className="font-mono tabular-nums text-ctu-text-muted text-xs">
                      0 (0%)
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
